"use client";

import { useEffect, useState } from "react";

/**
 * Ko-fi Floating Widget Component
 * Displays a floating Ko-fi button in the bottom-right corner
 * Only shows on desktop devices (hidden on mobile)
 */
export default function KofiFloatingWidget() {
  const [isMobile, setIsMobile] = useState(true); // Start as true to avoid flash

  useEffect(() => {
    // Check if device is mobile
    const checkIfMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };

    // Initial check
    checkIfMobile();

    const addTitleToKofiIFrame = () => {
      const iframes = document.querySelectorAll("iframe");
      iframes.forEach((iframe) => {
        const src = iframe.src || "";
        const id = iframe.id || "";
        const name = iframe.name || "";
        if ((src.includes("ko-fi") || id.includes("kofi") || name.includes("kofi")) && !iframe.title) {
          iframe.title = "Support FinanceLives on Ko-fi";
        }
      });
    };

    let titleInterval: NodeJS.Timeout;
    let listenerAdded = false;

    const loadKofi = () => {
      // Don't load Ko-fi widget on mobile devices
      if (window.innerWidth > 768) {
        // Load Ko-fi overlay widget script if not already loaded
        if (!document.getElementById("kofi-overlay-script")) {
          const script = document.createElement("script");
          script.id = "kofi-overlay-script";
          script.src = "https://storage.ko-fi.com/cdn/scripts/overlay-widget.js";
          script.async = true;
          document.body.appendChild(script);

          script.onload = () => {
            // Initialize Ko-fi overlay widget after script loads
            if (typeof (window as any).kofiWidgetOverlay !== "undefined") {
              (window as any).kofiWidgetOverlay.draw("financelives", {
                type: "floating-chat",
                "floating-chat.donateButton.text": "Support",
                "floating-chat.donateButton.background-color": "#10b981",
                "floating-chat.donateButton.text-color": "#fff",
              });
              setTimeout(addTitleToKofiIFrame, 500);
            }
          };
        } else if (typeof (window as any).kofiWidgetOverlay !== "undefined") {
          // Script already loaded, just draw the widget
          (window as any).kofiWidgetOverlay.draw("financelives", {
            type: "floating-chat",
            "floating-chat.donateButton.text": "Support",
            "floating-chat.donateButton.background-color": "#10b981",
            "floating-chat.donateButton.text-color": "#fff",
          });
          setTimeout(addTitleToKofiIFrame, 500);
        }

        // Periodically check and enforce title for accessibility
        titleInterval = setInterval(addTitleToKofiIFrame, 2000);
      }
      removeKofiListeners();
    };

    const removeKofiListeners = () => {
      if (!listenerAdded) return;
      window.removeEventListener("scroll", loadKofi);
      window.removeEventListener("mousemove", loadKofi);
      window.removeEventListener("touchstart", loadKofi);
      listenerAdded = false;
    };

    // Load Ko-fi widget only after user interacts
    if (window.innerWidth > 768) {
      window.addEventListener("scroll", loadKofi, { passive: true });
      window.addEventListener("mousemove", loadKofi, { passive: true });
      window.addEventListener("touchstart", loadKofi, { passive: true });
      listenerAdded = true;
    }

    // Add resize listener
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
      removeKofiListeners();
      if (titleInterval) clearInterval(titleInterval);
    };
  }, []);

  // Don't render anything on mobile
  if (isMobile) return null;

  return (
    <>
      <style jsx global>{`
        /* Ko-fi Floating Widget Custom Styles */
        #kofiShortcode iframe,
        div[style*="z-index: 1000000"] iframe {
          max-width: 80px !important;
          width: 80px !important;
        }

        /* Position Ko-fi widget to bottom-right instead of bottom-left */
        div[style*="z-index: 1000000"] {
          left: auto !important;
          right: 20px !important;
        }

        /* Adjust button size on mobile */
        @media (max-width: 768px) {
          #kofiShortcode iframe,
          div[style*="z-index: 1000000"] iframe {
            max-width: 70px !important;
            width: 70px !important;
          }
        }
      `}</style>
    </>
  );
}
